import { Info } from "lucide-react";
import type React from "react";
import { getNodeColorSwatchClass } from "@/features/graph/utils/colorSwatch";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";

interface InformationPanelProps {
	selectedNode: VisualNode | null;
	selectedEdge: VisualEdge | null;
	visible: boolean;
	onToggle: () => void;
}

/**
 * Information Panel component
 * Displays detailed information about the selected node or edge
 */
export const InformationPanel: React.FC<InformationPanelProps> = ({
	selectedNode,
	selectedEdge,
	visible,
	onToggle,
}) => {
	if (!visible) {
		return (
			<button
				type="button"
				onClick={onToggle}
				className="surface border border-subtle rounded-lg shadow-sm p-3 mb-4 soft-hover transition-colors flex items-center gap-2"
			>
				<Info className="w-4 h-4 text-muted" />
				<span className="text-sm font-medium text-muted">Show Info</span>
			</button>
		);
	}

	const hasSelection = selectedNode !== null;

	return (
		<div className="surface border border-subtle rounded-lg shadow-sm p-4 mb-4">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-2">
					<Info className="w-5 h-5 text-muted" />
					<h3 className="text-sm font-semibold text-primary">Information</h3>
				</div>
				<button type="button" onClick={onToggle} className="text-sm text-subtle hover-text transition-colors">
					Hide
				</button>
			</div>

			{!hasSelection ? (
				<div className="text-sm text-subtle text-center py-4">Select a node or edge to view details</div>
			) : selectedNode ? (
				<div className="space-y-3 p-3 rounded-md surface-muted border border-strong">
					<div>
						<div className="text-xs font-medium text-subtle mb-1">Name</div>
						<div className="text-sm font-semibold text-primary">{selectedNode.name}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">ID</div>
						<div className="text-sm text-muted font-mono break-all">{selectedNode.id}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Graph Element ID</div>
						<div className="text-sm text-muted font-mono break-all">{selectedNode.graphElementId}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Element Type</div>
						<div className="text-sm text-muted">{selectedNode.elementType}</div>
					</div>

					{selectedNode.typeId && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Type ID</div>
							<div className="text-sm text-muted font-mono break-all">{selectedNode.typeId}</div>
						</div>
					)}

					{selectedNode.color && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Color</div>
							<div className="flex items-center gap-2">
								<div className={getNodeColorSwatchClass(selectedNode.color)} />
								<div className="text-sm text-muted font-mono">{selectedNode.color}</div>
							</div>
						</div>
					)}

					{selectedNode.group !== undefined && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Community</div>
							<div className="text-sm text-muted">{selectedNode.group}</div>
						</div>
					)}

					{selectedNode.radius !== undefined && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Radius</div>
							<div className="text-sm text-muted">{selectedNode.radius.toFixed(2)}</div>
						</div>
					)}

					{selectedNode.metadata && Object.keys(selectedNode.metadata).length > 0 && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Metadata</div>
							<div className="text-sm text-muted surface-alt p-2 rounded font-mono max-h-32 overflow-y-auto">
								<pre className="whitespace-pre-wrap text-xs">{JSON.stringify(selectedNode.metadata, null, 2)}</pre>
							</div>
						</div>
					)}
				</div>
			) : selectedEdge ? (
				<div className="space-y-3 p-3 rounded-md surface-muted border border-strong">
					<div>
						<div className="text-xs font-medium text-subtle mb-1">Label</div>
						<div className="text-sm font-semibold text-primary">{selectedEdge.label}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">ID</div>
						<div className="text-sm text-muted font-mono break-all">{selectedEdge.id}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Source</div>
						<div className="text-sm text-muted font-mono break-all">
							{typeof selectedEdge.source === "string" ? selectedEdge.source : selectedEdge.source.id}
						</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Target</div>
						<div className="text-sm text-muted font-mono break-all">
							{typeof selectedEdge.target === "string" ? selectedEdge.target : selectedEdge.target.id}
						</div>
					</div>

					{selectedEdge.predicateId && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Predicate ID</div>
							<div className="text-sm text-muted font-mono break-all">{selectedEdge.predicateId}</div>
						</div>
					)}

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Tuple Position</div>
						<div className="text-sm text-muted">{selectedEdge.tuplePosition}</div>
					</div>

					<div>
						<div className="text-xs font-medium text-subtle mb-1">Type</div>
						<div className="text-sm text-muted">
							{selectedEdge.inferred ? (
								<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
									Inferred
								</span>
							) : (
								<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
									Extracted
								</span>
							)}
						</div>
					</div>

					{selectedEdge.strength !== undefined && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Strength</div>
							<div className="text-sm text-muted">{selectedEdge.strength.toFixed(2)}</div>
						</div>
					)}

					{selectedEdge.distance !== undefined && (
						<div>
							<div className="text-xs font-medium text-subtle mb-1">Distance</div>
							<div className="text-sm text-muted">{selectedEdge.distance.toFixed(2)}</div>
						</div>
					)}
				</div>
			) : null}
		</div>
	);
};

export default InformationPanel;
